import {
  getAllUsersSubscriptions,
  getAllUserRoles,
  getAllCompanies,
  adminCreateUser,
  adminUpdateUser,
} from '../../actions/AdminActions'
import { deleteUser } from '../../actions/UserActions'
import AddUserDialog from '../../components/AddUserDialog/AddUserDialog'
import CityAccessDialog from '../../components/CityAccessDialog/CityAccessDialog'
import ColumnVisibilityButton from '../../components/ColumnVisibilityButton/ColumnVisibilityButton'
import EditUserDialog from '../../components/EditUserDialog/EditUserDialog'
import TableToolbar from '../../components/TableToolbar/TableToolbar'
import { roleToDisplay } from '../../constants/roles'
import { useTableControls, TableColumn } from '../../hooks/useTableControls'
import {
  Add as AddIcon,
  Search as SearchIcon,
  Email as EmailIcon,
  Business as BusinessIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material'
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useState, useMemo, useCallback } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'

interface CityInfo {
  readonly [key: string]: number | string
  readonly id: number
  readonly name: string
  readonly country: string
  readonly province: string
}

interface UserRow {
  readonly [key: string]: unknown
  readonly id: number
  readonly companyId: number
  readonly firstName: string
  readonly lastName: string
  readonly email: string
  readonly title: string
  readonly phone: string
  readonly role: string
  readonly organization: string
  readonly isPrimaryContact: boolean
  readonly dateAssigned: string
  readonly cities: CityInfo[]
}

interface ImmutableEntry {
  get: (key: string) => ImmutableEntry
  toJS: () => Record<string, unknown>
  find: (fn: (item: ImmutableEntry) => boolean) => ImmutableEntry | undefined
  forEach: (fn: (item: ImmutableEntry) => void) => void
}

interface AdminStoreShape {
  get: (key: string) => ImmutableEntry & { size?: number }
}

const transformUsers = (adminStore: AdminStoreShape): UserRow[] => {
  const allUsersSubscriptions = adminStore.get('allUsersSubscriptions')
  const allUserRoles = adminStore.get('allUserRoles')
  const allCitiesImmutable = adminStore.get('allCities')
  const allCompaniesImmutable = adminStore.get('allCompanies')

  const cityMap = new Map<
    number,
    { name: string; country: string; province: string }
  >()
  allCitiesImmutable.forEach((city: ImmutableEntry) => {
    const c = city.toJS() as {
      id: number
      name: string
      country: string
      province: string | null
    }
    cityMap.set(c.id, {
      name: c.name,
      country: c.country,
      province: c.province ?? '',
    })
  })

  const companyMap = new Map<
    number,
    { name: string; primaryContactId: number | null }
  >()
  allCompaniesImmutable.forEach((company: ImmutableEntry) => {
    const co = company.toJS() as {
      id: number
      name: string
      primary_contact_id: number | null
    }
    companyMap.set(co.id, {
      name: co.name,
      primaryContactId: co.primary_contact_id,
    })
  })

  const getRoleValue = (roleId: number): string => {
    const match = allUserRoles.find(
      (r: ImmutableEntry) => (r.get('id') as unknown as number) === roleId
    )
    if (match) return String(match.get('title'))
    return String(roleId)
  }

  const users: UserRow[] = []
  allUsersSubscriptions.forEach((entry: ImmutableEntry) => {
    const user = entry.get('user')
    /*
     * A subscription entry can be null (a synthetic subscription that fails a
     * non-null field in the GraphQL schema comes back as null), so guard it.
     */
    const subs = entry.get('subscriptions').toJS() as unknown as ({
      city_id: number
    } | null)[]
    const cities: CityInfo[] = subs
      .map((s) => {
        if (!s) return undefined
        const info = cityMap.get(s.city_id)
        if (!info) return undefined
        return {
          id: s.city_id,
          name: info.name,
          country: info.country,
          province: info.province,
        }
      })
      .filter((c): c is CityInfo => c !== undefined)

    const companyId = user.get('company_id') as unknown as number
    const companyInfo = companyMap.get(companyId)
    users.push({
      id: user.get('id') as unknown as number,
      companyId,
      firstName: String(user.get('first_name')),
      lastName: String(user.get('last_name')),
      email: String(user.get('email')),
      title: String(user.get('occupation')),
      phone: String(user.get('phone')),
      role: getRoleValue(user.get('role_id') as unknown as number),
      organization: companyInfo?.name ?? 'Unknown',
      isPrimaryContact:
        companyInfo?.primaryContactId === (user.get('id') as unknown as number),
      dateAssigned: String(user.get('date_assigned')),
      cities,
    })
  })

  return users
}

interface UsersProps {
  readonly actions: {
    readonly getAllUsersSubscriptions: () => Promise<void>
    readonly getAllUserRoles: () => Promise<void>
    readonly getAllCompanies: () => Promise<void>
    readonly adminCreateUser: (
      company_id: number,
      firstName: string,
      lastName: string,
      email: string,
      phone: string,
      occupation: string,
      role_id: number,
      isPrimaryContact: boolean
    ) => Promise<unknown>
    readonly adminUpdateUser: (
      id: number,
      firstName: string,
      lastName: string,
      occupation: string,
      email: string,
      phone: string,
      role_id: number,
      company_id: number,
      isPrimaryContact: boolean
    ) => Promise<unknown>
  }
  readonly adminStore: AdminStoreShape
  readonly userStore: {
    readonly get: (key: string) => unknown
  }
}

const Users = ({ actions, adminStore, userStore }: UsersProps) => {
  const intl = useIntl()
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedUsers, setSelectedUsers] = useState<number[]>([])
  const [organizationFilter, setOrganizationFilter] = useState('')
  const [cityFilter, setCityFilter] = useState('')
  const [cityModalUser, setCityModalUser] = useState<UserRow | null>(null)
  const [loading, setLoading] = useState(true)
  const [isAddUserOpen, setIsAddUserOpen] = useState(false)
  const [editingUser, setEditingUser] = useState<UserRow | null>(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [errorSnackbar, setErrorSnackbar] = useState('')
  const [initError, setInitError] = useState<string | null>(null)

  useEffect(() => {
    Promise.all([
      actions.getAllUsersSubscriptions(),
      actions.getAllUserRoles(),
      actions.getAllCompanies(),
    ])
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Failed to load user data:', error)
        setInitError(String(error))
      })
      .finally(() => setLoading(false))
  }, [actions])

  const users = useMemo(() => {
    const subs = adminStore.get('allUsersSubscriptions')
    const roles = adminStore.get('allUserRoles')
    const companies = adminStore.get('allCompanies')
    if (!subs.size || !roles.size || !companies.size) return []
    return transformUsers(adminStore)
  }, [adminStore])

  const companies = useMemo(() => {
    const allCompanies = adminStore.get('allCompanies')
    if (!allCompanies.size) return []
    const result: { id: number; name: string }[] = []
    allCompanies.forEach((c: ImmutableEntry) => {
      const co = c.toJS() as { id: number; name: string }
      result.push({ id: co.id, name: co.name })
    })
    return result
  }, [adminStore])

  const allOrganizations = useMemo(
    () =>
      Array.from(new Set(users.map((u) => u.organization))).sort((a, b) =>
        a.localeCompare(b)
      ),
    [users]
  )

  const allCityNames = useMemo(
    () =>
      Array.from(
        new Set(users.flatMap((u) => u.cities.map((c) => c.name)))
      ).sort((a, b) => a.localeCompare(b)),
    [users]
  )

  const filteredUsers = useMemo(() => {
    const q = searchQuery.toLowerCase()
    return users.filter((user) => {
      const matchesSearch =
        !q ||
        user.firstName.toLowerCase().includes(q) ||
        user.lastName.toLowerCase().includes(q) ||
        user.email.toLowerCase().includes(q) ||
        user.title.toLowerCase().includes(q) ||
        user.organization.toLowerCase().includes(q)
      const matchesOrg =
        !organizationFilter || user.organization === organizationFilter
      const matchesCity =
        !cityFilter || user.cities.some((c) => c.name === cityFilter)
      return matchesSearch && matchesOrg && matchesCity
    })
  }, [users, searchQuery, organizationFilter, cityFilter])

  const defaultColumns: TableColumn[] = useMemo(
    () => [
      {
        id: 'firstName',
        label: intl.formatMessage({ id: 'users.table.firstName' }),
        visible: true,
        sortable: true,
      },
      {
        id: 'lastName',
        label: intl.formatMessage({ id: 'users.table.lastName' }),
        visible: true,
        sortable: true,
      },
      {
        id: 'email',
        label: intl.formatMessage({ id: 'users.table.email' }),
        visible: true,
        sortable: true,
      },
      {
        id: 'title',
        label: intl.formatMessage({ id: 'users.table.title' }),
        visible: true,
        sortable: true,
      },
      {
        id: 'phone',
        label: intl.formatMessage({ id: 'users.table.phone' }),
        visible: true,
        sortable: true,
      },
      {
        id: 'organization',
        label: intl.formatMessage({ id: 'users.table.organization' }),
        visible: true,
        sortable: true,
      },
      {
        id: 'dateAssigned',
        label: intl.formatMessage({ id: 'users.table.dateAssigned' }),
        visible: true,
        sortable: true,
      },
      {
        id: 'cityAccess',
        label: intl.formatMessage({ id: 'users.table.cityAccess' }),
        visible: true,
        sortable: false,
      },
    ],
    [intl]
  )

  const {
    page,
    rowsPerPage,
    columns,
    visibleColumns,
    paginatedData,
    sortBy,
    sortDirection,
    handleChangePage,
    handleChangeRowsPerPage,
    toggleColumn,
    handleSort,
  } = useTableControls(filteredUsers, defaultColumns, {
    defaultRowsPerPage: 10,
    defaultSortBy: 'dateAssigned',
    defaultSortDirection: 'desc',
  })

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      setSelectedUsers(checked ? filteredUsers.map((u) => u.id) : [])
    },
    [filteredUsers]
  )

  const handleSelectUser = useCallback((userId: number) => {
    setSelectedUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    )
  }, [])

  const handleDeleteSelected = useCallback(async () => {
    setDeleting(true)
    try {
      await Promise.all(selectedUsers.map(async (id) => deleteUser(id)))
      setSelectedUsers([])
      setShowDeleteConfirm(false)
      await actions.getAllUsersSubscriptions()
      await actions.getAllCompanies()
    } finally {
      setDeleting(false)
    }
  }, [selectedUsers, actions])

  const handleAddUser = useCallback(
    async (userData: {
      readonly companyId: number
      readonly firstName: string
      readonly lastName: string
      readonly email: string
      readonly phone: string
      readonly occupation: string
      readonly role_id: number
      readonly isPrimaryContact: boolean
    }) => {
      await actions.adminCreateUser(
        userData.companyId,
        userData.firstName,
        userData.lastName,
        userData.email,
        userData.phone,
        userData.occupation,
        userData.role_id,
        userData.isPrimaryContact
      )
      await actions.getAllUsersSubscriptions()
      await actions.getAllCompanies()
    },
    [actions]
  )

  const handleUpdateUser = useCallback(
    async (userData: {
      readonly id: number
      readonly firstName: string
      readonly lastName: string
      readonly email: string
      readonly phone: string
      readonly occupation: string
      readonly role_id: number
      readonly companyId: number
      readonly isPrimaryContact: boolean
    }) => {
      await actions.adminUpdateUser(
        userData.id,
        userData.firstName,
        userData.lastName,
        userData.occupation,
        userData.email,
        userData.phone,
        userData.role_id,
        userData.companyId,
        userData.isPrimaryContact
      )
      await actions.getAllUsersSubscriptions()
      await actions.getAllCompanies()
    },
    [actions]
  )

  const handleDownload = useCallback(() => {
    const csvEscape = (val: string) => {
      if (val.includes(',') || val.includes('"') || val.includes('\n')) {
        return `"${val.replace(/"/gu, '""')}"`
      }
      return val
    }

    const headers = visibleColumns.map((col) => csvEscape(col.label)).join(',')

    const rows = filteredUsers
      .map((user) =>
        visibleColumns
          .map((col) => {
            if (col.id === 'cityAccess') {
              return csvEscape(user.cities.map((c) => c.name).join('; '))
            }
            if (col.id === 'role') {
              return csvEscape(roleToDisplay(String(user[col.id])))
            }
            return csvEscape(String(user[col.id]))
          })
          .join(',')
      )
      .join('\n')

    const csv = `${headers}\n${rows}`
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = window.URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = `users_${new Date().toISOString().split('T')[0]}.csv`
    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)
    window.URL.revokeObjectURL(url)
  }, [filteredUsers, visibleColumns])

  const renderCell = useCallback(
    (column: TableColumn, user: UserRow) => {
      if (column.id === 'email') {
        return (
          <TableCell key={column.id}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <EmailIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                {user.email}
              </Typography>
            </Box>
          </TableCell>
        )
      }
      if (column.id === 'organization') {
        return (
          <TableCell key={column.id}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BusinessIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                {user.organization}
              </Typography>
            </Box>
          </TableCell>
        )
      }
      if (column.id === 'cityAccess') {
        return (
          <TableCell key={column.id} sx={{ whiteSpace: 'nowrap' }}>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'nowrap',
                gap: 0.5,
                cursor: 'pointer',
              }}
              onClick={(e) => {
                e.stopPropagation()
                setCityModalUser(user)
              }}
            >
              {user.cities.slice(0, 3).map((city, index) => (
                <Chip
                  key={`${user.id}-${city.name}-${index}`}
                  label={city.name}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              ))}
              {user.cities.length > 3 && (
                <Chip
                  label={intl.formatMessage(
                    { id: 'users.cityChip.more' },
                    { count: user.cities.length - 3 }
                  )}
                  size="small"
                  variant="outlined"
                />
              )}
            </Box>
          </TableCell>
        )
      }
      if (column.id === 'dateAssigned') {
        const date = new Date(user.dateAssigned)
        return (
          <TableCell key={column.id}>
            <Typography variant="body2" color="text.secondary">
              {!isNaN(date.getTime())
                ? date.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })
                : '-'}
            </Typography>
          </TableCell>
        )
      }
      const value = String(user[column.id] ?? '')
      return (
        <TableCell key={column.id}>
          <Typography variant="body2">
            {column.id === 'role' ? roleToDisplay(value) : value}
          </Typography>
        </TableCell>
      )
    },
    [intl]
  )

  if (!userStore.get('userInfo') || !userStore.get('subscriptions')) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  if (initError) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Typography variant="h6" color="error">
          <FormattedMessage id="users.error.loadFailed" />
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {initError}
        </Typography>
        <Button variant="contained" onClick={() => window.location.reload()}>
          <FormattedMessage id="users.retry" />
        </Button>
      </Box>
    )
  }

  const isTableLoading = loading || users.length === 0

  return (
    <Box
      sx={{
        maxWidth: 1800,
        mx: 'auto',
        p: { xs: 2, sm: 3, md: 4 },
        pt: { xs: 10, md: 4 },
        color: '#fff',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          mb: 4,
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{ fontSize: { xs: '1.75rem', md: '2.125rem' } }}
          >
            Users
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage users and their access to city data
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="large"
          startIcon={<AddIcon />}
          onClick={() => setIsAddUserOpen(true)}
          sx={{ width: { xs: '100%', sm: 'auto' }, color: '#000' }}
        >
          <FormattedMessage id="users.addUser" />
        </Button>
      </Box>

      {/* Main Card */}
      <Card>
        <CardContent>
          {/* Selected Users Banner */}
          {selectedUsers.length > 0 && (
            <Box
              sx={{
                mb: 2,
                p: 2,
                bgcolor: 'rgba(170, 209, 48, 0.1)',
                border: '1px solid rgba(170, 209, 48, 0.3)',
                borderRadius: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography
                variant="body1"
                sx={{ color: '#c4e056', fontWeight: 500 }}
              >
                {intl.formatMessage(
                  { id: 'users.selected' },
                  { count: selectedUsers.length }
                )}
              </Typography>
              <Button
                variant="contained"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => {
                  const currentUserId = (
                    userStore.get('userInfo') as ImmutableEntry
                  ).get('id') as unknown as number
                  if (selectedUsers.includes(currentUserId)) {
                    setErrorSnackbar(
                      intl.formatMessage({ id: 'users.error.deleteSelf' })
                    )
                    return
                  }
                  setShowDeleteConfirm(true)
                }}
              >
                <FormattedMessage id="users.deleteSelected" />
              </Button>
            </Box>
          )}

          {/* Search + Download + Column Visibility */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
            <TextField
              fullWidth
              disabled={isTableLoading}
              placeholder={intl.formatMessage({
                id: 'users.searchPlaceholder',
              })}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
              disabled={isTableLoading}
              sx={{ minWidth: 'auto', whiteSpace: 'nowrap' }}
            >
              <FormattedMessage id="users.download" />
            </Button>
            <ColumnVisibilityButton
              columns={columns}
              onToggleColumn={toggleColumn}
              disabled={isTableLoading}
            />
          </Box>

          {/* Filters */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: 2,
              mb: 3,
            }}
          >
            <FormControl fullWidth disabled={isTableLoading}>
              <InputLabel id="organization-filter-label">
                <FormattedMessage id="users.filter.organization" />
              </InputLabel>
              <Select
                labelId="organization-filter-label"
                value={organizationFilter}
                onChange={(e) => setOrganizationFilter(e.target.value)}
                label={intl.formatMessage({ id: 'users.filter.organization' })}
              >
                <MenuItem value="">
                  <FormattedMessage id="users.filter.allOrganizations" />
                </MenuItem>
                {allOrganizations.map((org) => (
                  <MenuItem key={org} value={org}>
                    {org}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth disabled={isTableLoading}>
              <InputLabel id="city-filter-label">
                <FormattedMessage id="users.filter.city" />
              </InputLabel>
              <Select
                labelId="city-filter-label"
                value={cityFilter}
                onChange={(e) => setCityFilter(e.target.value)}
                label={intl.formatMessage({ id: 'users.filter.city' })}
              >
                <MenuItem value="">
                  <FormattedMessage id="users.filter.allCities" />
                </MenuItem>
                {allCityNames.map((city) => (
                  <MenuItem key={city} value={city}>
                    {city}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Table */}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={
                        paginatedData.length > 0 &&
                        selectedUsers.length === filteredUsers.length
                      }
                      indeterminate={
                        selectedUsers.length > 0 &&
                        selectedUsers.length < filteredUsers.length
                      }
                      onChange={(e) => handleSelectAll(e.target.checked)}
                    />
                  </TableCell>
                  {visibleColumns.map((column) => (
                    <TableCell key={column.id}>
                      {column.sortable !== false ? (
                        <TableSortLabel
                          active={sortBy === column.id}
                          direction={
                            sortBy === column.id ? sortDirection : 'asc'
                          }
                          onClick={() => handleSort(column.id)}
                        >
                          {column.label}
                        </TableSortLabel>
                      ) : (
                        column.label
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {isTableLoading ? (
                  <TableRow>
                    <TableCell
                      colSpan={visibleColumns.length + 1}
                      sx={{ py: 6, textAlign: 'center', border: 0 }}
                    >
                      <CircularProgress size={28} />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 1 }}
                      >
                        Loading users...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : null}
                {!loading && users.length > 0 && paginatedData.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={visibleColumns.length + 1}
                      sx={{ py: 6, textAlign: 'center', border: 0 }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        No users found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : null}
                {!loading &&
                  users.length > 0 &&
                  paginatedData.length > 0 &&
                  paginatedData.map((user: UserRow) => (
                    <TableRow
                      key={user.id}
                      hover
                      onClick={() => setEditingUser(user)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell
                        padding="checkbox"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Checkbox
                          checked={selectedUsers.includes(user.id)}
                          onChange={() => handleSelectUser(user.id)}
                        />
                      </TableCell>
                      {visibleColumns.map((column) => renderCell(column, user))}
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TableToolbar
            page={page}
            rowsPerPage={rowsPerPage}
            totalCount={filteredUsers.length}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
          />
        </CardContent>
      </Card>

      {/* City Access Dialog */}
      {cityModalUser !== null && (
        <CityAccessDialog
          userName={`${cityModalUser.firstName} ${cityModalUser.lastName}`}
          cities={cityModalUser.cities}
          onClose={() => setCityModalUser(null)}
        />
      )}

      {/* Add User Dialog */}
      <AddUserDialog
        open={isAddUserOpen}
        onClose={() => setIsAddUserOpen(false)}
        onAddUser={handleAddUser}
        companies={companies}
      />

      {/* Edit User Dialog */}
      <EditUserDialog
        user={editingUser}
        onClose={() => setEditingUser(null)}
        onUpdateUser={handleUpdateUser}
        companies={companies}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        PaperProps={{
          sx: { backgroundColor: '#31353D', backgroundImage: 'none' },
        }}
      >
        <DialogTitle>
          <FormattedMessage id="users.confirmDelete.title" />
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <FormattedMessage id="users.confirmDelete.message" />
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowDeleteConfirm(false)}
            disabled={deleting}
          >
            <FormattedMessage id="users.confirmDelete.cancel" />
          </Button>
          <Button
            onClick={() => void handleDeleteSelected()}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting
              ? intl.formatMessage({ id: 'users.confirmDelete.deleting' })
              : intl.formatMessage({ id: 'users.confirmDelete.confirm' })}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error Snackbar */}
      <Snackbar
        open={errorSnackbar !== ''}
        autoHideDuration={5000}
        onClose={() => setErrorSnackbar('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={() => setErrorSnackbar('')}
          severity="error"
          variant="filled"
        >
          {errorSnackbar}
        </Alert>
      </Snackbar>
    </Box>
  )
}

const mapStateToProps = (state: {
  UserStore: UsersProps['userStore']
  AdminStore: UsersProps['adminStore']
}) => ({
  userStore: state.UserStore,
  adminStore: state.AdminStore,
})

const mapDispatchToProps = (
  dispatch: Parameters<typeof bindActionCreators>[1]
) => ({
  actions: bindActionCreators(
    {
      getAllUsersSubscriptions,
      getAllUserRoles,
      getAllCompanies,
      adminCreateUser,
      adminUpdateUser,
    },
    dispatch
  ),
})

// eslint-disable-next-line @typescript-eslint/no-unsafe-call -- react-redux connect returns any
const ConnectedUsers = connect(
  mapStateToProps,
  mapDispatchToProps
)(Users) as React.ComponentType

export default ConnectedUsers
